import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../utils/api';
import { Loader2, Calendar, FileText, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const resolveUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const NewsList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    document.title = 'Анализи & Новини — Deus Estate';
    fetchPosts();
  }, [search]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ published: 'true', limit: '100' });
      if (search) params.append('search', search);
      const data = await apiClient.get(`/api/blog/?${params}`, { skipAuth: true });
      setPosts(data);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20">
      <section className="container-luxury mb-12">
        <div className="text-center mb-10">
          <h1 className="heading-luxury mb-4">
            Анализи & <span className="gradient-text">новини</span>
          </h1>
          <p className="text-lg text-[#A9A9A9] max-w-2xl mx-auto">
            Експертни мнения и актуални анализи от пазара на имоти
          </p>
        </div>

        <div className="relative max-w-md mx-auto mb-12">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A9A9A9]" />
          <Input
            placeholder="Търсене..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#0B0B0B] border-[#D4AF37]/20 text-[#F6F2E8]"
            data-testid="public-news-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-[#A9A9A9]">Все още няма публикувани статии.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="public-news-grid">
            {posts.map((p) => (
              <Link
                key={p._id}
                to={`/news/${p.slug}`}
                className="group bg-[#111111] border border-[#D4AF37]/10 rounded-2xl overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-300"
                data-testid={`public-post-${p.slug}`}
              >
                <div className="h-48 bg-gradient-to-br from-[#D4AF37]/20 to-[#B8892E]/20 overflow-hidden">
                  {p.featured_image ? (
                    <img
                      src={resolveUrl(p.featured_image)}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><FileText className="w-12 h-12 text-[#D4AF37]/40" /></div>
                  )}
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex items-center gap-1 text-xs text-[#A9A9A9]">
                    <Calendar size={12} />
                    <span>{new Date(p.published_at || p.created_at).toLocaleDateString('bg-BG')}</span>
                  </div>
                  <h3 className="font-serif font-semibold text-[#F6F2E8] text-lg line-clamp-2 group-hover:text-[#D4AF37] transition-colors">{p.title}</h3>
                  {p.excerpt && <p className="text-sm text-[#A9A9A9] line-clamp-3">{p.excerpt}</p>}
                  {p.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-2">
                      {p.tags.slice(0, 3).map(t => (
                        <span key={t} className="text-[10px] px-2 py-0.5 bg-[#0B0B0B] rounded text-[#D4AF37]">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default NewsList;
